<script setup>
import { ref, computed, useId } from 'vue';
import { useI18n } from 'vue-i18n';
import Popover from 'dashboard/components-next/popover/Popover.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TmkFilterFields from './TmkFilterFields.vue';
import {
  emptyTmkFilters,
  normalizeTmkFilters,
} from 'dashboard/helper/tmkFiltersHelper';

const props = defineProps({
  modelValue: { type: Object, required: true },
  defaults: { type: Object, default: () => ({}) },
  labels: { type: Array, default: () => [] },
  agents: { type: Array, default: () => [] },
  teams: { type: Array, default: () => [] },
  currentUserId: { type: [String, Number], default: '' },
  assignedOnly: { type: Boolean, default: false },
  sortBy: { type: String, default: '' },
  showSearch: { type: Boolean, default: true },
  includeUnclassified: { type: Boolean, default: false },
  showAdvanced: { type: Boolean, default: false },
  advancedCount: { type: Number, default: 0 },
  validateExtra: { type: Function, default: () => true },
});
const emit = defineEmits(['apply', 'advanced']);
const { t } = useI18n();
const popoverRef = ref(null);
const fieldsRef = ref(null);
const draft = ref(emptyTmkFilters());
const draftSort = ref('');
const revision = ref(0);
const panelId = `tmk-filters-${useId()}`;
const onOpen = () => {
  draft.value = normalizeTmkFilters(
    JSON.parse(JSON.stringify(props.modelValue))
  );
  draftSort.value = props.sortBy;
  revision.value += 1;
};
const open = () => popoverRef.value?.show();
const close = () => popoverRef.value?.hide();
const clear = () => {
  draft.value = normalizeTmkFilters({
    ...emptyTmkFilters(),
    ...JSON.parse(JSON.stringify(props.defaults)),
  });
  revision.value += 1;
};
const apply = () => {
  const fieldsValid = fieldsRef.value?.validate() ?? true;
  const extraValid = props.validateExtra(draft.value);
  if (!fieldsValid || !extraValid) return;
  emit('apply', normalizeTmkFilters(draft.value), draftSort.value);
  close();
};
const editAdvanced = () => {
  close();
  emit('advanced');
};
const sorts = [
  'last_activity_at_desc',
  'last_activity_at_asc',
  'created_at_desc',
  'created_at_asc',
  'waiting_since_asc',
  'waiting_since_desc',
  'priority_desc',
  'priority_asc',
  'priority_desc_created_at_asc',
  'unread',
];
const advancedLabel = computed(
  () =>
    `${t('CHAT_LIST.TMK.ADVANCED_RULES')}${props.advancedCount ? ` (${props.advancedCount})` : ''}`
);
defineExpose({ open });
</script>

<template>
  <Popover ref="popoverRef" align="end" disable-mobile-view @show="onOpen">
    <template #default="{ isOpen }">
      <slot name="trigger" :is-open="isOpen" :panel-id="panelId">
        <Button
          type="button"
          faded
          slate
          :label="t('CHAT_LIST.TMK.FILTERS')"
          :aria-expanded="isOpen"
          :aria-controls="panelId"
        />
      </slot>
    </template>
    <template #content="{ availableHeight }">
      <form
        :id="panelId"
        role="dialog"
        aria-modal="false"
        :aria-labelledby="`${panelId}-title`"
        :aria-describedby="`${panelId}-help`"
        data-testid="tmk-floating-panel"
        :style="{ maxHeight: availableHeight }"
        class="flex w-[min(34rem,calc(100vw-2rem))] flex-col rounded-xl bg-n-solid-1"
        @submit.prevent="apply"
      >
        <div class="shrink-0 border-b border-n-weak px-4 pb-3 pt-4">
          <h2
            :id="`${panelId}-title`"
            class="m-0 text-base font-semibold text-n-slate-12"
          >
            {{ t('CHAT_LIST.TMK.FILTERS') }}
          </h2>
          <p :id="`${panelId}-help`" class="mb-0 mt-1 text-xs text-n-slate-10">
            {{ t('CHAT_LIST.TMK.FILTER_DIALOG_HELP') }}
          </p>
        </div>
        <div
          :key="revision"
          class="flex max-h-[min(32rem,calc(100vh-13rem))] min-h-0 flex-col gap-5 overflow-y-auto overscroll-contain px-4 py-4"
        >
          <slot name="before" :draft="draft" />
          <TmkFilterFields
            ref="fieldsRef"
            v-model="draft"
            :labels="labels"
            :agents="agents"
            :teams="teams"
            :current-user-id="currentUserId"
            :assigned-only="assignedOnly"
            :show-search="showSearch"
            :include-unclassified="includeUnclassified"
          />
          <label
            v-if="sortBy"
            class="flex flex-col gap-1.5 text-sm font-medium text-n-slate-12"
          >
            {{ t('CHAT_LIST.TMK.SORT') }}
            <select
              v-model="draftSort"
              class="!m-0 !h-9 !rounded-lg !border-n-weak !text-sm"
            >
              <option v-for="sort in sorts" :key="sort" :value="sort">
                {{ t(`CHAT_LIST.SORT_ORDER_ITEMS.${sort}.TEXT`) }}
              </option>
            </select>
          </label>
          <slot name="extra" :draft="draft" />
          <button
            v-if="showAdvanced"
            type="button"
            class="self-start text-sm text-n-blue-11 hover:underline"
            @click="editAdvanced"
          >
            {{ advancedLabel }}
          </button>
        </div>
        <div
          class="flex shrink-0 items-center gap-2 border-t border-n-weak px-4 py-3"
        >
          <Button
            type="button"
            faded
            slate
            :label="t('CHAT_LIST.TMK.CLEAR_DRAFT')"
            @click="clear"
          />
          <div class="ml-auto flex gap-2">
            <Button
              type="button"
              faded
              slate
              :label="t('DIALOG.BUTTONS.CANCEL')"
              @click="close"
            />
            <Button
              type="submit"
              solid
              teal
              :label="t('CHAT_LIST.TMK.APPLY')"
              data-testid="tmk-apply-filters"
            />
          </div>
        </div>
      </form>
    </template>
  </Popover>
</template>
